import { FC, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import styles from './app-edit-price-fields.module.scss';
import { serviceAreas } from '../../services/service-areas';
import { useTranslation } from 'react-i18next';
import Field from '../../components/shared/Field/Field';
import Modal from '../../components/shared/Modal/Modal';
import BtButton from '../bt-button/bt-button';
import { NotificationManager } from 'react-notifications';
import { Add, TrashIcon } from '../../components/svgs';
import NotesPencilIcon from '../../components/svgs/notesPencilIcon';
import Selector from '../../components/shared/Selector/Selector';
import appState from '../../state/AppStateContainer';
import RecategorizeModal from '../app-supplier-database/recategorizeModal';
import DataTable from '../../components/shared/DataTable/DataTable';
import OptionsMenu from '../../components/shared/OptionsMenu/OptionsMenu';

type Area = {
  name: string;
  _id: string;
  isActive: boolean;
  accountNumber?: string;
  slug?: string;
}

type CategoryItem = {
  label: string;
  value: string; // value is the id of the category/area
  accountNumber?: string;
  linkedCategory?: string[]; // Array of linked super admin category names
  slug?: string;
}

type LocationState = {
  addNewAreaModal?: boolean;
}

const AppEditCategories : FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [superAdminCategories, setSuperAdminCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories'});
  const [t2] = useTranslation('supplierDatabase');
  const [addNewAreaModal, setAddNewAreaModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryAccountNumber, setNewCategoryAccountNumber] = useState('');
  const [linkedCategory, setLinkedCategory] = useState<CategoryItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [modalRecategorizeShow, setModalRecategorizeShow] = useState(false);
  const [modalRecategorizeKey, setModalRecategorizeKey] = useState(0);
  const [editModalShow, setEditModalShow] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryAccountNumber, setEditCategoryAccountNumber] = useState('');

  useEffect(() => {
    if (location.state?.addNewAreaModal) {
      setAddNewAreaModal(true);
    }

    (async () => {
      const [allAreas, allSuperAdminAreas] = await Promise.all([
        serviceAreas.getAreas(),
        serviceAreas.fetchSuperAdminAreas()
      ]);

      let mappedCategories = (allAreas ?? []).map((x: Area) => ({
        label: x.name,
        value: x._id,
        accountNumber: x.accountNumber,
        linkedCategory: [] as string[],
        slug: x.slug
      })) as CategoryItem[];


      allSuperAdminAreas?.forEach((item: any) => {
        item.customerAreas?.forEach((x: any) => {
          const categoryIndex = mappedCategories.findIndex(c => c.value === x.areaId);
          if (categoryIndex !== -1) {
            if (!mappedCategories[categoryIndex].linkedCategory) {
              mappedCategories[categoryIndex].linkedCategory = [];
            }
            mappedCategories[categoryIndex].linkedCategory!.push(item.name);
          }
        });
      });

      setCategories(mappedCategories);

      setSuperAdminCategories(allSuperAdminAreas?.map((x: Area) => ({
        label: x.name,
        value: x._id,
      })) ?? []);

      setLoading(false);
    })();

  }, []);

  const categoryAlreadyExists = () => {
    if (!newCategoryName.trim()) return false;
    return categories.some(
      cat => cat.label.trim().toLowerCase() === newCategoryName.trim().toLowerCase()
    ) || superAdminCategories.some(
      cat => cat.label.trim().toLowerCase() === newCategoryName.trim().toLowerCase()
    );
  };

  const updateCategoryLinks = async (
    customerAreaId: string,
    currentLinks: string[],
    newLinks: CategoryItem[]
  ) => {
    const currentLinkIds = currentLinks
      .map(name => superAdminCategories.find(cat => cat.label === name)?.value)
      .filter(Boolean) as string[];

    const newLinkIds = newLinks.map(cat => cat.value);

    const toLink = newLinkIds.filter(id => !currentLinkIds.includes(id));
    const toUnlink = currentLinkIds.filter(id => !newLinkIds.includes(id));

    for (const superAdminId of toLink) {
      await serviceAreas.linkCategoryToSuperAdminCategory(superAdminId, customerAreaId);
    }

    for (const superAdminId of toUnlink) {
      await serviceAreas.unlinkCategoryFromSuperAdminCategory(superAdminId, customerAreaId);
    }
  };

  const handleSaveNewCategory = async () => {
    if (!newCategoryName.trim() || categoryAlreadyExists()) return;

    setSaving(true);
    try {
      const updatedAreas = await serviceAreas.addArea({
        name: newCategoryName.trim(),
        accountNumber: newCategoryAccountNumber.trim() || undefined
      }) as Area[] | null;

      if (updatedAreas && Array.isArray(updatedAreas)) {
        const addedCategory = updatedAreas.find(x => x.name === newCategoryName);

        if (addedCategory) {
          await updateCategoryLinks(addedCategory._id, [], linkedCategory);

          NotificationManager.success(t2('notification.success.addArea', 'Yrkeskategori tillagd'));

          setCategories(updatedAreas.map((x: Area) => ({
            label: x.name,
            value: x._id,
            accountNumber: x.accountNumber,
            linkedCategory: x._id === addedCategory._id ? linkedCategory.map(cat => cat.label) : []
          })));
        }

        // Reset form and close modal
        setNewCategoryName('');
        setNewCategoryAccountNumber('');
        setAddNewAreaModal(false);
        setLinkedCategory([]);
        // Clear the location state to prevent reopening the modal
        if (location.state?.addNewAreaModal) {
          history.replace({ ...location, state: {} });
        }
      }
    } catch (error) {
      console.error('Error adding category:', error);
      NotificationManager.error(t2('notification.error.addArea', 'Kunde inte lägga till yrkeskategori'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setAddNewAreaModal(false);
    setNewCategoryName('');
    setNewCategoryAccountNumber('');
    setLinkedCategory([]);
    // Clear the location state to prevent reopening the modal
    if (location.state?.addNewAreaModal) {
      history.replace({ ...location, state: {} });
    }
  };

  const handleCloseEditModal = () => {
    setLinkedCategory([]);
    setEditModalShow(false);
    setEditingCategory(null);
    setEditCategoryName('');
    setEditCategoryAccountNumber('');
  };

  const handleOpenEditModal = (category: CategoryItem) => {
    setEditingCategory(category);
    setEditCategoryName(category.label);
    setEditCategoryAccountNumber(category.accountNumber || '');
    setLinkedCategory(category.linkedCategory
      ? superAdminCategories.filter(cat => category.linkedCategory?.includes(cat.label))
      : []
    );
    setEditModalShow(true);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editCategoryName.trim()) return;

    setSaving(true);
    try {
      await serviceAreas.updateAreaDetails(editingCategory.value, {
        name: editCategoryName.trim(),
        accountNumber: editCategoryAccountNumber.trim() || undefined
      });

      const currentLinks = editingCategory.linkedCategory || [];
      await updateCategoryLinks(editingCategory.value, currentLinks, linkedCategory);

      setCategories(prev => {
        const index = prev.findIndex(p => p.value === editingCategory.value);
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          label: editCategoryName.trim(),
          accountNumber: editCategoryAccountNumber.trim() || undefined,
          linkedCategory: linkedCategory.map(cat => cat.label)
        };
        return updated;
      });

      NotificationManager.success(t('notification.success.edit', 'Kategori uppdaterad'));
      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating category:', error);
      NotificationManager.error(t('notification.error.edit', 'Kunde inte uppdatera kategori'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: CategoryItem) => {
    if (!window.confirm(t('delete.confirm', 'Är du säker på att du vill ta bort {{name}}?', { name: category.label }))) {
      return;
    }

    try {
      await serviceAreas.deleteArea(category.value);
      setCategories(prev => prev.filter(p => p.value !== category.value));
      NotificationManager.success(t('notification.success.delete', 'Kategori borttagen'));
    } catch (error) {
      console.error('Error deleting category:', error);
      NotificationManager.error(t('notification.error.delete', 'Kunde inte ta bort kategori'));
    }
  };

  const onAddArea = () => {
    setAddNewAreaModal(true);
  }

  const url = appState._getCurrentOrgUrl();
  const isSuperAdminOrganization = appState.isSuperAdmin() && appState.getCurrentOrgSlug() === 'superadmin';

  const handleRowClick = (_event?: React.MouseEvent, rowData?: CategoryItem) => {
    if (isSuperAdminOrganization && rowData?.slug) {
      history.push(`${url}/categories/${rowData.slug}`);
    }
  };

  const tableColumns = [
    {
      title: t('columns.name', 'Namn'),
      field: 'label',
      render: (rowData: CategoryItem) => (
        <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
          {rowData.label}
        </span>
      )
    },
    {
      title: t('columns.accountNumber', 'Kontonummer'),
      field: 'accountNumber',
      render: (rowData: CategoryItem) => (
        <span style={{ color: 'var(--gray-700)' }}>{rowData.accountNumber || '–'}</span>
      )
    },
    {
      title: t('columns.linkedCategories', 'Kopplade globala kategorier'),
      field: 'linkedCategory',
      sorting: false,
      render: (rowData: CategoryItem) => (
        <span style={{ color: 'var(--gray-700)' }}>
          {rowData.linkedCategory && rowData.linkedCategory.length > 0
            ? rowData.linkedCategory.join(', ')
            : '–'
          }
        </span>
      )
    },
    {
      title: '',
      field: 'actions',
      sorting: false,
      width: '50px',
      render: (rowData: CategoryItem) => (
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu
            options={[
              {
                label: t('menu.suppliers', 'Anslut leverantörer'),
                action: () => {
                  history.push(`${url}/area/${rowData.value}/suppliers`, {
                    area: { label: rowData.label, value: rowData.value }
                  });
                }
              },
              {
                label: t('menu.edit', 'Redigera'),
                action: () => handleOpenEditModal(rowData),
                icon: <NotesPencilIcon size="14px" color="var(--gray-700)" />
              },
              {
                label: t('menu.delete', 'Ta bort'),
                action: () => handleDelete(rowData),
                icon: <TrashIcon size="14px" color="var(--gray-700)" />
              }
            ]}
          />
        </div>
      )
    }
  ];

  return <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
    <p className={styles.intro}>
      {t('intro', 'Här kan du byta namn på eller ta bort yrkeskategorier. Namnbyten påverkar både befintliga upphandlingar och leverantörer. Borttagna kategorier försvinner från leverantörer men befintliga upphandlingar påverkas inte.')}
    </p>
      {appState.isManagementRole() && (
        <BtButton
          class="white"
          onClick={() => {
            setModalRecategorizeShow(true);
            setModalRecategorizeKey(modalRecategorizeKey + 1);
          }}
          style={{ border: '1px solid var(--gray-300)' }}
        >
          {t('button.recategorize', 'Omkategorisera')}
        </BtButton>
      )}
    <BtButton color='white' onClick={onAddArea}>
      <Add fill="var(--orange-500)" size="16px" />
      <span>{t('button.addArea', 'Lägg till yrkeskategori')}</span>
    </BtButton>
    </div>

    <div style={{ marginTop: '24px' }} className={styles.row}>
      <DataTable
        isLoading={loading}
        columns={tableColumns}
        data={categories}
        defaultOrderBy={0}
        defaultOrderDirection="asc"
        defaultOrderByField="label"
        onRowClick={isSuperAdminOrganization ? handleRowClick : undefined}
      />
    </div>

    <Modal
      title={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
      show={addNewAreaModal}
      setShow={handleCloseModal}
      hasCloseButton
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px 0' }}>
        <Field
          label={t('edit.categoryNameLabel', 'Kategorinamn')}
          value={newCategoryName}
          onChange={(value) => setNewCategoryName(value)}
          placeholder={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
          required
          errorMessage={
            categoryAlreadyExists()
              ? t2('addArea.tooltip.areaAlreadyExists', 'Kategori finns redan')
              : undefined
          }
        />
        <Field
          label={t2('addArea.label.accountNumber', 'Kontonummer')}
          value={newCategoryAccountNumber}
          onChange={(value) => setNewCategoryAccountNumber(value)}
          placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
        />
        <Selector
            isDisabled={loading}
            label={t('edit.linkToSuperAdminCategory', 'Länka till global kategori')}
            placeholder={t('edit.selectPlaceholder', 'Välj')}
            isMulti={true}
            options={superAdminCategories}
            onChange={(selected) => {
              setLinkedCategory(selected as CategoryItem[]);
            }}
            value={linkedCategory}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
          <BtButton
            color="white"
            onClick={handleCloseModal}
            disabled={saving}
          >
            {t('edit.cancel', 'Avbryt')}
          </BtButton>

          <BtButton
            color="primary"
            onClick={handleSaveNewCategory}
            disabled={!newCategoryName.trim() || categoryAlreadyExists() || saving}
            loaderShow={saving}
          >
            {saving ? t2('addArea.button.saving', 'Sparar...') : t2('addArea.button.save', 'Spara yrkeskategori')}
          </BtButton>
        </div>
      </div>
    </Modal>

    <Modal
      title={t('edit.modalTitle', 'Redigera kategori')}
      show={editModalShow}
      setShow={handleCloseEditModal}
      hasCloseButton
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px 0' }}>
        <Field
          label={t('edit.categoryNameLabel', 'Kategorinamn')}
          value={editCategoryName}
          onChange={(value) => setEditCategoryName(value)}
          required
        />
        <Field
          label={t('edit.accountNumberLabel', 'Kontonummer')}
          value={editCategoryAccountNumber}
          onChange={(value) => setEditCategoryAccountNumber(value)}
          placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
        />
        <Selector
          label={t('edit.linkedCategoryLabel', 'Kopplat till global kategori')}
          options={superAdminCategories}
          isMulti={true}
          onChange={(value) => {
            setLinkedCategory(value as CategoryItem[]);
          }}
          value={linkedCategory}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
          <BtButton
            color="white"
            onClick={handleCloseEditModal}
            disabled={saving}
          >
            {t('edit.cancel', 'Avbryt')}
          </BtButton>

          <BtButton
            color="primary"
            onClick={handleSaveEdit}
            disabled={!editCategoryName.trim() || saving}
            loaderShow={saving}
          >
            {saving ? t('edit.saving', 'Sparar...') : t('edit.save', 'Spara')}
          </BtButton>
        </div>
      </div>
    </Modal>

    <RecategorizeModal
      show={modalRecategorizeShow}
      setShow={setModalRecategorizeShow}
      key={modalRecategorizeKey}
      areas={categories}
      onSave={() => { }}
    />
  </div>;
}

export default AppEditCategories;
